import { AIGPT } from "@kemis/harvey-ai";
import { InputNumber } from "primereact/inputnumber";
import { useCallback, useEffect, useMemo, useState } from "react";

// Icons
import { TbBadge } from "react-icons/tb";

// Types
import type {
	IAssistant,
	IAssistantThreadMessage,
	TThreadRunStatus,
} from "@kemis/harvey-ai/@types";
import { useAuthContext } from "~/hooks/contexts/Auth";
import { debounce } from "~/utils/debounce";
import type { IExtraProps } from "./types";

export function Extra({
	setCondominiumAmount,
	dispatcher,
	setExtraAmount,
	timeToBuildSell,
	condominiumAmount,
	ADM_FEE,
	CERTIFICATIONS,
	ITBI,
	REGISTER,
	TOTAL_BUILD,
	TOTAL_TERRAIN,
	extraAmount,
	REP_PROJECT,
	REP_BUILD_DATA,
	REP_HOUSES,
}: IExtraProps) {
	const { user } = useAuthContext();
	const [assistant, setAssistant] = useState<IAssistant | null>(null);
	const [assistants, setAssistants] = useState<IAssistant[]>([]);
	const [messages, setMessage] = useState<IAssistantThreadMessage[]>([]);
	const [threadStatus, setThreadStatus] =
		useState<TThreadRunStatus>("completed");
	const [onThread, setOnThread] = useState<boolean>(false);

	// AUX Variables
	const DEBOUNCE_TIME_ALREADY_PROMPT = 225;
	// biome-ignore lint/correctness/useExhaustiveDependencies: Its not necessary to add all dependencies here
	const HARVEY_AI = useMemo(
		() =>
			new AIGPT(
				user,
				null,
				[],
				"rep-chat-assitant",
				"gpt-4o",
				(process.env?.REACT_APP_OPENAI_CHAT_GTP as string) || undefined,
			),
		[],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Its not necessary to add all dependencies here
	const REP_FILE = useMemo(() => {
		console.table({
			RESIDENTES_LOCALIZACAO: REP_PROJECT?.origin_residents || "N/A",
			RESIDENTES_IDADE_MEDIO: REP_PROJECT?.average_age_residents || "N/A",
			RESIDENTES_SALARIO_MEDIO: REP_PROJECT?.average_income_residents || 0,
			LOCALIZACAO: REP_PROJECT?.localization || "N/A",
			VGV: REP_BUILD_DATA?.houseAmount || 0,
			ESTILO_DE_CASA: REP_PROJECT?.standard?.standard || "N/A",
			TAMANHO_TERRENO: REP_BUILD_DATA?.idealArea || 0,
			TAMANHO_AREA_CONSTRUIDA: REP_BUILD_DATA?.idealAreaBuild || 0,
			TEMPO_DE_OBRA_E_VENDA: `${timeToBuildSell} meses`,
			TAXA_ADM: ADM_FEE,
			CERTIDAO: CERTIFICATIONS,
			REGISTRO: REGISTER,
			ITBI: ITBI,
			VALOR_TOTAL_DA_CONSTRUCAO: TOTAL_BUILD,
			VALOR_TOTAL_DO_TERRENO: TOTAL_TERRAIN,
			CUSTO_OBRA: extraAmount,
		});

		const FILE_BIN = new Blob(
			[
				JSON.stringify({
					RESIDENTES_LOCALIZACAO: REP_PROJECT?.origin_residents || "N/A",
					RESIDENTES_IDADE_MEDIO: REP_PROJECT?.average_age_residents || "N/A",
					RESIDENTES_SALARIO_MEDIO: REP_PROJECT?.average_income_residents || 0,
					LOCALIZACAO: REP_PROJECT?.localization || "N/A",
					VGV: REP_BUILD_DATA?.houseAmount || 0,
					ESTILO_DE_CASA: REP_PROJECT?.standard?.standard || "N/A",
					TAMANHO_TERRENO: REP_BUILD_DATA?.idealArea || 0,
					TAMANHO_AREA_CONSTRUIDA: REP_BUILD_DATA?.idealAreaBuild || 0,
					TEMPO_DE_OBRA_E_VENDA: `${timeToBuildSell} meses`,
					TAXA_ADM: ADM_FEE,
					CERTIDAO: CERTIFICATIONS,
					REGISTRO: REGISTER,
					ITBI: ITBI,
					VALOR_TOTAL_DA_CONSTRUCAO: TOTAL_BUILD,
					VALOR_TOTAL_DO_TERRENO: TOTAL_TERRAIN,
					CUSTO_OBRA: extraAmount,
				}),
			],
			{
				type: "application/json",
			},
		);

		const FILE = new File([FILE_BIN], "rep.json");

		return FILE;
	}, [
		REP_PROJECT,
		REP_BUILD_DATA,
		REP_HOUSES,
		timeToBuildSell,
		extraAmount,
		condominiumAmount,
		ADM_FEE,
		CERTIFICATIONS,
		ITBI,
		REGISTER,
		TOTAL_BUILD,
		TOTAL_TERRAIN,
	]);

	/**
	 * Set a assistant and close the UI List
	 */
	// biome-ignore lint/correctness/useExhaustiveDependencies: Its not necessary to add all dependencies here
	const handlerOnSetAssistant = useCallback(
		(assistantSelected: IAssistant | null = null) => {
			if (!assistantSelected) return;

			setAssistant(assistantSelected);
		},
		[setAssistant],
	);

	/**
	 * Create a new thread of conversation
	 */
	const handleOnSetNewThread = useCallback(async () => {
		try {
			if (!HARVEY_AI || !assistant) return;

			if (HARVEY_AI.threadID && !(await HARVEY_AI.setDeleteThread()).deleted)
				throw new Error("The last thread cannot be deleted");

			HARVEY_AI.setCreateThread();
		} catch (error) {
			//
		} finally {
			//
		}
	}, [HARVEY_AI, assistant]);

	/**
	 * Delete the actual thread of conversation
	 */
	const handleOnSetDeleteThread = useCallback(async () => {
		try {
			if (!HARVEY_AI) return;

			if (HARVEY_AI.threadID && !(await HARVEY_AI.setDeleteThread()).deleted)
				throw new Error("The last thread cannot be deleted");
		} catch (error) {
			// do anything
		}
	}, [HARVEY_AI]);

	/**
	 * Get all assistants from API KEY and set a default (first) assistant as default
	 */

	// biome-ignore lint/correctness/useExhaustiveDependencies: Its not necessary to add all dependencies here
	const getAssistants = useCallback(async () => {
		try {
			const chat_assistants = await HARVEY_AI.getChatAssitants();

			if (!chat_assistants) throw new Error("");

			setAssistants(chat_assistants.data as unknown as IAssistant[]);

			handlerOnSetAssistant(
				(chat_assistants.data as unknown as IAssistant[])[0],
			);
		} catch (error) {
			// do anything
		}
	}, [setAssistants, setAssistant]);

	/**
	 * Send a mensage to actual thread and set the message state with all text's (I/O) from thread
	 */
	const handleOnSetNewThreadPrompts = useCallback(
		async (promptData) => {
			try {
				setOnThread(true);

				if (!HARVEY_AI) throw new Error("The HARVEY_AI statement is missing");

				if (!promptData) throw new Error("The prompt is missing");

				const PROMPT_VALUE = promptData;

				if (!PROMPT_VALUE) throw new Error("The prompt value is not valid");

				const FILE_THREAD = await HARVEY_AI.setCreateFile(REP_FILE);

				console.table({
					FILE_THREAD,
					REP_FILE,
					FILE: await REP_FILE.text(),
					// FILE_URL: URL.createObjectURL(REP_FILE)
				});

				const THREAD_PROMPTS = ((
					await (
						await HARVEY_AI.setCreateMessage(PROMPT_VALUE, "user", [
							{
								file_id: FILE_THREAD.id,
								tools: [
									{
										type: "file_search",
									},
									{
										type: "code_interpreter",
									},
								],
							},
						])
					)
						.onThreadStream((args: TThreadRunStatus) => {
							console.warn("CALLBACK WAS CALLED ON EVENT STREAM", args);

							setThreadStatus(args);
						})
						.setRunThread()
				)?.data || []) as unknown as IAssistantThreadMessage[];

				// console.table(THREAD_PROMPTS)
				setMessage(THREAD_PROMPTS);
			} catch (error) {
				console.table(error);
			} finally {
				setOnThread(false);
			}
		},
		[HARVEY_AI, REP_FILE],
	);

	/**
	 * Get all assistant and stop the speech events
	 */
	// biome-ignore lint/correctness/useExhaustiveDependencies: Its not necessary to add all dependencies here
	useEffect(() => {
		if (assistants.length >= 1 || assistant) return;

		getAssistants();
	}, []);

	/**
	 * EVERYTIME THAT "ASSISTANT STATE" CHANGE, WILL BE DELETED THE ACTUAL/PREV THREAD
	 * AND CREATED A NEW THREAD BASED ON NEW ASSISTANT
	 *
	 * */
	// biome-ignore lint/correctness/useExhaustiveDependencies: Its not necessary to add all dependencies here
	useEffect(() => {
		if (!assistant || !REP_FILE) return;

		debounce(DEBOUNCE_TIME_ALREADY_PROMPT, () => {
			handleOnSetNewThread().then((_r) => {
				console.log("ASSISTANT", assistant.id);
				HARVEY_AI?.set("assistantID", assistant.id);

				const PROMPT = "Usando os dados do arquivo (Não os use para encontrar os gastos), estime e ou infere os seguintes gastos: Luz e Água, IPTU (Anualmente), Averbação, ISS, INSS e Habite-se. Gere uma lista contendo os valores e seus respectivos identificadores, aplique um destaque colorido para diferenciar os valores e também informe no valor se foi uma estimativa ou inferido (coloque a fonte). Não precisa informar se houve algum problema com o arquivo";
				handleOnSetNewThreadPrompts(PROMPT);
			});
		});

		return () => {
			handleOnSetDeleteThread();
		};
	}, [assistant, REP_FILE]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Its not necessary to add all dependencies here
	useEffect(() => {
		if (condominiumAmount <= 0) {
			setExtraAmount(0);

			return;
		}

		setExtraAmount(() => condominiumAmount + dispatcher);

		return () => {
			setExtraAmount(0);
		};
	}, [condominiumAmount, timeToBuildSell]);

	return (
		<span
			style={{
				maxWidth: "calc(50% - 0.75rem)",
				minWidth: "calc(50% - 0.75rem)",
			}}
			className="max-sm:!w-full max-sm:!min-w-full max-sm:!max-w-ful md:flex-1 h-auto flex flex-col justify-between gap-3
				rounded-xl
				border-slate-200 border-[1px]
				bg-white shadow-md overflow-hidden
			"
		>
			<span className="w-full flex flex-row items-center gap-2 px-4 pt-4 pb-2 border-slate-200 border-b-[1px]">
				<TbBadge size={25} />
				<span className="font-semibold">Custus Extras</span>
			</span>

			<span className="w-full h-auto flex flex-col gap-3 p-4">
				<span className="w-full h-auto flex flex-row items-center justify-between gap-4">
					<span>Condominio</span>
					<InputNumber
						mode="currency"
						currency="BRL"
						locale="pt-BR"
						minFractionDigits={2}
						value={condominiumAmount}
						onValueChange={(e) => setCondominiumAmount(e.value || 0)}
					/>
				</span>

				{/* <span className="w-full h-auto flex flex-row items-center justify-between">
					<span>Luz e Água</span>
					<span className="font-semibold">
						{
							Intl.NumberFormat('pt-BR', {
								style: 'currency',
								currency: 'BRL',
								currencyDisplay: 'symbol'
							}).format(0)
						}
					</span>
				</span>

				<span className="w-full h-auto flex flex-row items-center justify-between">
					<span>IPTU ANUAL</span>
					<span className="font-semibold">
						{
							Intl.NumberFormat('pt-BR', {
								style: 'currency',
								currency: 'BRL',
								currencyDisplay: 'symbol'
							}).format(0)
						}
					</span>
				</span>

				<span className="w-full h-auto flex flex-row items-center justify-between">
					<span>Habite-se</span>
					<span className="font-semibold">
						{
							Intl.NumberFormat('pt-BR', {
								style: 'currency',
								currency: 'BRL',
								currencyDisplay: 'symbol'
							}).format(0)
						}
					</span>
				</span>

				<span className="w-full h-auto flex flex-row items-center justify-between">
					<span>Averbação</span>
					<span className="font-semibold">
						{
							Intl.NumberFormat('pt-BR', {
								style: 'currency',
								currency: 'BRL',
								currencyDisplay: 'symbol'
							}).format(0)
						}
					</span>
				</span>

				<span className="w-full h-auto flex flex-row items-center justify-between">
					<span>ISS</span>
					<span className="font-semibold">
						{
							Intl.NumberFormat('pt-BR', {
								style: 'currency',
								currency: 'BRL',
								currencyDisplay: 'symbol'
							}).format(0)
						}
					</span>
				</span>

				<span className="w-full h-auto flex flex-row items-center justify-between">
					<span>INSS</span>
					<span className="font-semibold">
						{
							Intl.NumberFormat('pt-BR', {
								style: 'currency',
								currency: 'BRL',
								currencyDisplay: 'symbol'
							}).format(0)
						}
					</span>
				</span> */}

				{messages.map((message) => {
					return (
						<span
							key={message.id}
							id={message.id}
							data-ismy={!message.assistant_id && true}
							data-fromassistant={!(!message.assistant_id && true)}
							className="
								w-full
								h-auto
								hidden
								data-[fromassistant=true]:flex
								text-md text-gray-900 font-normal
								transition-all
								duration-[0.15s]
								will-change-auto
								relative
							"
						>
							<span
								// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
								dangerouslySetInnerHTML={{
									__html: message.content[0].text.value,
								}}
							/>
						</span>
					);
				})}

				<span className="w-full h-auto flex flex-row items-center justify-between">
					<span>Despachante</span>
					<span className="font-semibold">
						{Intl.NumberFormat("pt-BR", {
							style: "currency",
							currency: "BRL",
							currencyDisplay: "symbol",
						}).format(dispatcher)}
					</span>
				</span>
			</span>

			<span className="w-[90%] self-end h-auto flex flex-col items-end gap-2 bg-slate-200 p-4">
				<span className="w-full flex flex-row items-center justify-end gap-5">
					<span className="font-bold">
						{Intl.NumberFormat("pt-BR", {
							style: "currency",
							currency: "BRL",
							currencyDisplay: "symbol",
						}).format(extraAmount)}
					</span>
				</span>
			</span>
		</span>
	);
}
